import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { axiosPrivate } from 'modules/networkTools/networkTools';

import type {
  CreateOrganizationPresetParams,
  DeletePresetParams,
  DeletePresetResponse,
  GetOrganizationPresetByIdParams,
  OrganizationPresetDetail,
  OrganizationPresetListItem,
  OrganizationProgramItem,
  OrganizationProgramsResponse,
  PresetsQueryParams,
  PresetsListResponse,
  PresetTriggerConfig,
  PresetTriggerFieldOption,
  UpdateOrganizationPresetParams,
  UpdatePresetStatusesParams,
  UpdatePresetStatusesResponse
} from './types';

type ApiErrorResponse = {
  errors?: string | string[];
  message?: string;
  error?: string;
};

type PresetTodoApiItem =
  | {
      id?: number;
      title?: string | null;
    }
  | string;

type PresetProgramApiItem =
  | {
      id?: string | number;
      name?: string | null;
    }
  | string;

type PresetsListApiItem = Omit<OrganizationPresetListItem, 'dotphrase'> & {
  dotphrase?: string;
  name?: string;
  todos?: PresetTodoApiItem[] | null;
  programs?: PresetProgramApiItem[] | null;
};

type PresetsListApiResponse = Omit<PresetsListResponse, 'content'> & {
  content: PresetsListApiItem[];
};

const getErrorMessage = (error: ApiErrorResponse | undefined, fallback: string): string => {
  const errors = error?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(', ');
  }

  if (typeof errors === 'string' && errors.trim().length > 0) {
    return errors;
  }

  if (typeof error?.message === 'string' && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error?.error === 'string' && error.error.trim().length > 0) {
    return error.error;
  }

  return fallback;
};

const getAxiosErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<ApiErrorResponse>;

  return getErrorMessage(axiosError.response?.data, axiosError.message || fallback);
};

const mapPresetTodos = (todos: PresetTodoApiItem[] | null | undefined) => {
  if (!Array.isArray(todos)) {
    return [];
  }

  return todos
    .map((todo) => {
      if (typeof todo === 'string') {
        const title = todo.trim();

        return title ? { title } : null;
      }

      const title = todo.title?.trim() ?? '';

      if (!title) {
        return null;
      }

      return {
        ...(todo.id ? { id: todo.id } : {}),
        title
      };
    })
    .filter((todo): todo is NonNullable<typeof todo> => Boolean(todo));
};

const mapPresetPrograms = (programs: PresetProgramApiItem[] | null | undefined) => {
  if (!Array.isArray(programs)) {
    return [];
  }

  return programs
    .map((program) => {
      if (typeof program === 'string') {
        const name = program.trim();

        return name ? { name } : null;
      }

      const name = program.name?.trim() ?? '';

      if (!name) {
        return null;
      }

      return {
        ...(typeof program.id !== 'undefined' ? { id: program.id } : {}),
        name
      };
    })
    .filter((program): program is NonNullable<typeof program> => Boolean(program));
};

const mapPresetListResponse = (response: PresetsListApiResponse): PresetsListResponse => ({
  ...response,
  content: response.content.map((preset) => ({
    id: preset.id,
    dotphrase: preset.dotphrase ?? preset.name ?? '',
    goal: preset.goal,
    todos: mapPresetTodos(preset.todos),
    programs: mapPresetPrograms(preset.programs),
    triggerFields: Array.isArray(preset.triggerFields) ? preset.triggerFields : [],
    triggerSummary: preset.triggerSummary ?? null,
    status: preset.status,
    isCore: preset.isCore
  }))
});

export const getOrganizationPresetsList = createAsyncThunk<
  PresetsListResponse,
  PresetsQueryParams,
  { rejectValue: string }
>('presets/getOrganizationPresetsList', async (params, { rejectWithValue }) => {
  const searchParams = new URLSearchParams({
    page: String(params.page),
    size: String(params.size)
  });

  if (params.search?.trim()) {
    searchParams.set('searchTerm', params.search.trim());
  }

  params.statuses?.forEach((status) => {
    searchParams.append('statuses', status);
  });

  params.triggerFields?.forEach((triggerField) => {
    searchParams.append('triggerFields', triggerField);
  });

  try {
    const response = await axiosPrivate.get(
      `setup/organization/${params.organizationId}/presets?${searchParams.toString()}`
    );
    const responseData = response.data as PresetsListApiResponse;

    return mapPresetListResponse(responseData);
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to load presets'));
  }
});

export const getOrganizationPresetById = createAsyncThunk<
  OrganizationPresetDetail,
  GetOrganizationPresetByIdParams,
  { rejectValue: string }
>('presets/getOrganizationPresetById', async (params, { rejectWithValue }) => {
  try {
    const response = await axiosPrivate.get(
      `setup/organization/${params.organizationId}/presets/${params.presetId}`
    );

    return response.data as OrganizationPresetDetail;
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to load preset'));
  }
});

export const getOrganizationPresetTriggerFields = createAsyncThunk<
  PresetTriggerFieldOption[],
  { organizationId: string },
  { rejectValue: string }
>('presets/getOrganizationPresetTriggerFields', async ({ organizationId }, { rejectWithValue }) => {
  try {
    const response = await axiosPrivate.get(
      `setup/organization/${organizationId}/presets/trigger-fields`
    );

    return response.data as PresetTriggerFieldOption[];
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to load trigger fields'));
  }
});

export const getOrganizationPresetTriggerConfig = createAsyncThunk<
  PresetTriggerConfig,
  { organizationId: string },
  { rejectValue: string }
>('presets/getOrganizationPresetTriggerConfig', async ({ organizationId }, { rejectWithValue }) => {
  try {
    const response = await axiosPrivate.get(
      `setup/organization/${organizationId}/presets/trigger-config`
    );

    return response.data as PresetTriggerConfig;
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to load trigger config'));
  }
});

export const getOrganizationPrograms = createAsyncThunk<
  OrganizationProgramItem[],
  { organizationId: string },
  { rejectValue: string }
>('presets/getOrganizationPrograms', async ({ organizationId }, { rejectWithValue }) => {
  const searchParams = new URLSearchParams({
    page: '0',
    size: '99999'
  });

  try {
    const response = await axiosPrivate.get(
      `setup/organization/${organizationId}/programs?${searchParams.toString()}`
    );

    const responseData = response.data as OrganizationProgramsResponse | OrganizationProgramItem[];

    if (Array.isArray(responseData)) {
      return responseData;
    }

    return Array.isArray(responseData.content) ? responseData.content : [];
  } catch (error) {
    const axiosError = error as AxiosError<{ errors?: string | string[] }>;
    const errors = axiosError.response?.data?.errors;

    if (Array.isArray(errors) && errors.length > 0) {
      return rejectWithValue(errors.join(', '));
    }

    if (typeof errors === 'string') {
      return rejectWithValue(errors);
    }

    if (axiosError.message) {
      return rejectWithValue(axiosError.message);
    }

    return rejectWithValue('Unable to load programs');
  }
});

export const createOrganizationPreset = createAsyncThunk<
  void,
  CreateOrganizationPresetParams,
  { rejectValue: string }
>('presets/createOrganizationPreset', async (params, { rejectWithValue }) => {
  try {
    await axiosPrivate.post(`setup/organization/${params.organizationId}/presets`, params.values);
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to add preset'));
  }
});

export const updateOrganizationPreset = createAsyncThunk<
  void,
  UpdateOrganizationPresetParams,
  { rejectValue: string }
>('presets/updateOrganizationPreset', async (params, { rejectWithValue }) => {
  try {
    await axiosPrivate.put(
      `setup/organization/${params.organizationId}/presets/${params.presetId}`,
      params.values
    );
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to update preset'));
  }
});

export const putOrganizationPresetStatuses = createAsyncThunk<
  UpdatePresetStatusesResponse,
  UpdatePresetStatusesParams,
  { rejectValue: string }
>('presets/putOrganizationPresetStatuses', async (params, { rejectWithValue }) => {
  try {
    const response = await axiosPrivate.put(
      `setup/organization/${params.organizationId}/presets/statuses`,
      {
        statusesByPresetId: params.statusesByPresetId
      }
    );

    return response.data as UpdatePresetStatusesResponse;
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to save preset statuses'));
  }
});

export const deleteOrganizationPreset = createAsyncThunk<
  DeletePresetResponse,
  DeletePresetParams,
  { rejectValue: string }
>('presets/deleteOrganizationPreset', async (params, { rejectWithValue }) => {
  try {
    const response = await axiosPrivate.delete(
      `setup/organization/${params.organizationId}/presets/${params.presetId}`
    );

    return response.data as DeletePresetResponse;
  } catch (error) {
    return rejectWithValue(getAxiosErrorMessage(error, 'Unable to delete preset'));
  }
});
